import { useState, useMemo, useEffect, useRef, memo, useCallback } from 'react';
import { Link as RouterLink, Navigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  Select,
  FormControl,
  Button,
} from '@mui/material';
import { Section, SectionCard, SectionHeader } from '@/components/common';
import { formatDateShort } from '@/utils/dateFormatter';
import { getErrorMessage } from '@/utils/errorMessage';
import { datePickerLocaleText } from '@/utils/datePickerLocaleText';
import type { SelectChangeEvent } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ClearIcon from '@mui/icons-material/Clear';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  useActiveJobEnds,
  useEligibleAuthors,
  useEditJobEndAuthor,
  useSendJobEndReminder,
  useReissueJobEnd,
  useDeleteJobEnd,
  useBulkSendReminder,
  useBulkReissueJobEnd,
  useBulkDeleteJobEnd,
} from '@/queries/useJobEnds';
import type { ActiveJobEnd, ActiveJobEndsFilterRequest, JobEndStatus } from '@/api/types/jobEnd.types';
import { buildBulkReminderMessage } from './bulkReminderMessage';
import { paths } from '@/routes/paths';
import { useAuthStore } from '@/stores/useAuthStore';

type SortDirection = 'asc' | 'desc';

interface Column {
  id: keyof ActiveJobEnd | 'actions';
  label: string;
  sortable: boolean;
  filterable: boolean;
  minWidth?: number;
}

const columns: Column[] = [
  { id: 'projectName', label: 'PROJECT NAME', sortable: true, filterable: true, minWidth: 200 },
  { id: 'dueDate', label: 'DUE DATE', sortable: true, filterable: true, minWidth: 110 },
  { id: 'performanceFlag', label: 'PERFORMANCE FLAG', sortable: true, filterable: true, minWidth: 100 },
  { id: 'appraiseeName', label: 'APPRAISEE', sortable: true, filterable: true, minWidth: 150 },
  { id: 'geo', label: 'GEO', sortable: true, filterable: true, minWidth: 100 },
  { id: 'tenureInGrade', label: 'TENURE IN GRADE', sortable: true, filterable: true, minWidth: 120 },
  { id: 'jobEndAuthor', label: 'JOB END AUTHOR', sortable: true, filterable: true, minWidth: 150 },
  { id: 'status', label: 'STATUS', sortable: true, filterable: true, minWidth: 120 },
  { id: 'actions', label: 'ACTIONS', sortable: false, filterable: false, minWidth: 80 },
];

const statusOptions: JobEndStatus[] = ['NotStarted', 'Started', 'Finalised', 'Delivered'];

// Sticky column widths (px) for pinned left/right columns
const CHECKBOX_COL_WIDTH = 48;
const ACTIONS_COL_MIN_WIDTH = 80;


function getStatusChipSx(status: JobEndStatus) {
  switch (status) {
    case 'NotStarted':
      return { bgcolor: '#EBEBEB', color: 'black' };
    case 'Started':
      return { bgcolor: '#9C27B0', color: 'white' };
    case 'Finalised':
      return { bgcolor: '#0288D1', color: 'white' };
    case 'Delivered':
      return { bgcolor: '#2E7D32', color: 'white' };
    case 'Expired':
      return { bgcolor: '#EF6C00', color: 'white' };
    case 'Deleted':
      return { bgcolor: '#D32F2F', color: 'white' };
    default:
      return { bgcolor: 'grey.400', color: 'white' };
  }
}


function formatStatusLabel(status: JobEndStatus): string {
  switch (status) {
    case 'NotStarted':
      return 'NOT STARTED';
    default:
      return status.toUpperCase();
  }
}

// Memoized filter row to prevent re-renders when data changes
interface FilterRowProps {
  canEdit: boolean;
  columns: Column[];
  inputValues: Record<string, string>;
  onFilterChange: (field: keyof ActiveJobEndsFilterRequest, value: string) => void;
  onClearFilter: (field: keyof ActiveJobEndsFilterRequest) => void;
  onDateChange: (value: string) => void;
  activeFilterCount: number;
  onClearAllFilters: () => void;
}

const FilterRow = memo(function FilterRow({
  canEdit,
  columns,
  inputValues,
  onFilterChange,
  onClearFilter,
  onDateChange,
  activeFilterCount,
  onClearAllFilters,
}: FilterRowProps) {
  return (
    <TableRow>
      {canEdit && (
        <TableCell padding="checkbox" sx={{ bgcolor: 'white', borderBottom: 1, borderColor: 'divider', position: 'sticky', left: 0, zIndex: 3 }} />
      )}
      {columns
        .filter((column) => canEdit || column.id !== 'actions')
        .map((column) => (
          <TableCell key={column.id} sx={{
            minWidth: column.minWidth,
            bgcolor: 'white',
            py: 1,
            borderBottom: 1,
            borderColor: 'divider',
            ...(column.id === 'projectName' && {
              position: 'sticky',
              left: canEdit ? CHECKBOX_COL_WIDTH : 0,
              zIndex: 3,
              boxShadow: '4px 0 8px -4px rgba(0,0,0,0.15)',
            }),
            ...(column.id === 'status' && {
              position: 'sticky',
              right: canEdit ? ACTIONS_COL_MIN_WIDTH : 0,
              zIndex: 3,
              boxShadow: '-4px 0 8px -4px rgba(0,0,0,0.15)',
            }),
            ...(column.id === 'actions' && {
              position: 'sticky',
              right: 0,
              zIndex: 3,
            }),
          }}>
            {column.filterable && column.id !== 'status' && column.id !== 'dueDate' && (
              <TextField
                size="small"
                placeholder="Type..."
                value={inputValues[column.id] ?? ''}
                onChange={(e) =>
                  onFilterChange(column.id as keyof ActiveJobEndsFilterRequest, e.target.value)
                }
                InputProps={{
                  endAdornment: inputValues[column.id] ? (
                    <IconButton
                      size="small"
                      onClick={() => onClearFilter(column.id as keyof ActiveJobEndsFilterRequest)}
                      sx={{ p: 0.5 }}
                    >
                      <ClearIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  ) : null,
                }}
                sx={{
                  width: '100%',
                  bgcolor: 'white',
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: 'divider' },
                  },
                }}
              />
            )}
            {column.id === 'dueDate' && (
              <LocalizationProvider dateAdapter={AdapterDayjs} localeText={datePickerLocaleText}>
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                  <DatePicker
                    value={inputValues.dueDate ? dayjs(inputValues.dueDate) : null}
                    onChange={(newValue) => {
                      const dateStr = newValue ? newValue.format('YYYY-MM-DD') : '';
                      onDateChange(dateStr);
                    }}
                    format="DD MMM YYYY"
                    slotProps={{
                      textField: {
                        size: 'small',
                        placeholder: 'Select...',
                        error: false,
                        sx: {
                          width: 140,
                          bgcolor: 'white',
                          '& .MuiInputBase-input': { fontSize: '0.75rem', p: '6px 8px' },
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': { borderColor: 'divider' },
                          },
                          '& .MuiInputAdornment-root': { ml: 0 },
                          '& .MuiIconButton-root': { p: 0.25 },
                        },
                      },
                    }}
                  />
                  {inputValues.dueDate && (
                    <IconButton
                      size="small"
                      onClick={() => onDateChange('')}
                      sx={{ p: 0, minWidth: 16 }}
                    >
                      <ClearIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  )}
                </Box>
              </LocalizationProvider>
            )}
            {column.id === 'status' && (
              <FormControl size="small" fullWidth>
                <Select
                  displayEmpty
                  value={inputValues[column.id] ?? ''}
                  onChange={(e: SelectChangeEvent) =>
                    onFilterChange(column.id as keyof ActiveJobEndsFilterRequest, e.target.value)
                  }
                  renderValue={(selected) => {
                    if (!selected) {
                      return <Typography component="span" sx={{ color: 'text.secondary' }}>Type...</Typography>;
                    }
                    return formatStatusLabel(selected as JobEndStatus);
                  }}
                  endAdornment={
                    inputValues[column.id] ? (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          onClearFilter(column.id as keyof ActiveJobEndsFilterRequest);
                        }}
                        sx={{ p: 0.5, mr: 2 }}
                      >
                        <ClearIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    ) : null
                  }
                  sx={{
                    bgcolor: 'white',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                  }}
                >
                  <MenuItem value="">
                    <em>All</em>
                  </MenuItem>
                  {statusOptions.map((status) => (
                    <MenuItem key={status} value={status}>
                      {formatStatusLabel(status)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {column.id === 'actions' && activeFilterCount > 1 && (
              <Link
                component="button"
                variant="body2"
                onClick={onClearAllFilters}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  color: 'error.main',
                  textDecoration: 'none',
                  whiteSpace: 'nowrap',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                Clear all <CloseIcon sx={{ fontSize: 16 }} />
              </Link>
            )}
          </TableCell>
        ))}
    </TableRow>
  );
});

export function JobEndAdminPage() {
  // Role-based access: Staffing = full edit, HR = read-only
  const permissions = useAuthStore((state) => state.permissions);
  const hasAnyRole = useAuthStore((state) => state.hasAnyRole);
  const canAccess = hasAnyRole(['Staffing', 'HR']);
  const hasRole = useAuthStore((state) => state.hasRole);
  const canEdit = hasRole('Staffing');

  // All hooks must be called before any conditional returns
  // Filter state - actual filters sent to API
  const [filters, setFilters] = useState<Partial<ActiveJobEndsFilterRequest>>({});
  // Local input values for immediate UI feedback (debounced before API call)
  const [inputValues, setInputValues] = useState<Record<string, string>>({});
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [sortBy, setSortBy] = useState<string>('projectName');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  // Selection state (only used when canEdit)
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  // Action menu state
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuJobEndId, setMenuJobEndId] = useState<number | null>(null);
  const [menuJobEndStatus, setMenuJobEndStatus] = useState<JobEndStatus | null>(null);

  // Edit mode state
  const [editingRowId, setEditingRowId] = useState<number | null>(null);
  const [selectedAuthorId, setSelectedAuthorId] = useState<number | null>(null);

  // Snackbar state
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Build filter request
  const filterRequest: ActiveJobEndsFilterRequest = useMemo(
    () => {
      const request = {
        page: page + 1,
        pageSize,
        sortBy,
        sortDirection,
        ...filters,
      };
      return request;
    },
    [page, pageSize, sortBy, sortDirection, filters]
  );

  // Query
  const { data, isLoading, isFetching, isError } = useActiveJobEnds(filterRequest);

  // Mutations
  const sendReminderMutation = useSendJobEndReminder();
  const reissueMutation = useReissueJobEnd();
  const deleteMutation = useDeleteJobEnd();
  const editAuthorMutation = useEditJobEndAuthor();
  const bulkSendReminderMutation = useBulkSendReminder();
  const bulkReissueMutation = useBulkReissueJobEnd();
  const bulkDeleteMutation = useBulkDeleteJobEnd();

  // Eligible authors for editing (fetched when editing)
  const { data: eligibleAuthors } = useEligibleAuthors(
    String(editingRowId ?? ''),
    editingRowId !== null
  );

  const rows = useMemo(() => data?.items ?? [], [data?.items]);
  const totalCount = data?.totalCount ?? 0;

  const handleFilterChange = useCallback((field: keyof ActiveJobEndsFilterRequest, value: string) => {
    // Update local input immediately for responsive UI
    setInputValues((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear previous debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Debounce the actual filter update (API call)
    debounceTimerRef.current = setTimeout(() => {
      setFilters((prev) => ({
        ...prev,
        [field]: value || undefined,
      }));
      setPage(0);
    }, 300);
  }, []);

  const handleClearFilter = useCallback((field: keyof ActiveJobEndsFilterRequest) => {
    // Clear debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    // Clear local input
    setInputValues((prev) => {
      const newValues = { ...prev };
      delete newValues[field];
      return newValues;
    });
    // Clear actual filter
    setFilters((prev) => {
      const newFilters = { ...prev };
      delete newFilters[field];
      return newFilters;
    });
    setPage(0);
  }, []);

  const handleDateChange = useCallback((value: string) => {
    setInputValues((prev) => ({ ...prev, dueDate: value }));

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      if (value === 'Invalid Date') return;
      const dateMatch = value.match(/^(\d{4})-\d{2}-\d{2}$/);
      if (dateMatch && parseInt(dateMatch[1], 10) < 1900) return;

      setFilters((prev) => {
        if (value) {
          return { ...prev, dueDateFrom: value, dueDateTo: value };
        }
        const f = { ...prev };
        delete f.dueDateFrom;
        delete f.dueDateTo;
        return f;
      });
      setPage(0);
    }, 300);
  }, []);

  const handleClearAllFilters = useCallback(() => {
    // Clear debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    // Clear all local inputs and filters
    setInputValues({});
    setFilters({});
    setPage(0);
  }, []);

  // Count active filters (non-empty values in inputValues)
  const activeFilterCount = useMemo(() => {
    return Object.values(inputValues).filter((v) => v && v.trim() !== '').length;
  }, [inputValues]);

  // Check if bulk delete should be disabled (contains Finalised or Delivered)
  const bulkDeleteDisabled = useMemo(() => {
    return selectedIds.some((id) => {
      const jobEnd = rows.find((row) => row.id === id);
      return jobEnd?.status === 'Finalised' || jobEnd?.status === 'Delivered';
    });
  }, [selectedIds, rows]);

  // Redirect if user doesn't have required roles
  if (permissions && !canAccess) {
    return <Navigate to={paths.unauthorized} replace />;
  }

  // Handlers
  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortDirection('asc');
    }
  };

  // Selection handlers
  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedIds(rows.map((row) => row.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id: number) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((selectedId) => selectedId !== id) : [...prev, id]
    );
  };

  const isSelected = (id: number) => selectedIds.includes(id);

  // Action menu handlers
  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, jobEnd: ActiveJobEnd) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuJobEndId(jobEnd.id);
    setMenuJobEndStatus(jobEnd.status);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
    setMenuJobEndId(null);
    setMenuJobEndStatus(null);
  };

  // Action handlers
  const handleSendReminder = async () => {
    if (!menuJobEndId) return;
    handleCloseMenu();
    try {
      await sendReminderMutation.mutateAsync(menuJobEndId);
      setSnackbar({ open: true, message: 'Reminder sent to the assigned Job End author.', severity: 'success' });
    } catch (error) {
      // The server refuses a single-row reminder for a departed appraisee with a specific reason, and
      // TerminationDate is not a column here - a generic failure would leave the admin unable to act.
      setSnackbar({ open: true, message: getErrorMessage(error), severity: 'error' });
    }
  };

  const handleReissue = async () => {
    if (!menuJobEndId) return;
    handleCloseMenu();
    try {
      await reissueMutation.mutateAsync({ id: menuJobEndId });
      setSnackbar({ open: true, message: 'Job End form reissued successfully.', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Failed to reissue Job End.', severity: 'error' });
    }
  };

  const handleDelete = async () => {
    if (!menuJobEndId) return;
    handleCloseMenu();
    try {
      await deleteMutation.mutateAsync(menuJobEndId);
      setSnackbar({ open: true, message: 'Job End form deleted successfully.', severity: 'success' });
      setSelectedIds((prev) => prev.filter((id) => id !== menuJobEndId));
    } catch {
      setSnackbar({ open: true, message: 'Failed to delete Job End.', severity: 'error' });
    }
  };

  const handleEdit = () => {
    if (!menuJobEndId) return;
    setEditingRowId(menuJobEndId);
    handleCloseMenu();
  };

  const handleAuthorSelect = (newAuthorStaffId: number) => {
    setSelectedAuthorId(newAuthorStaffId);
  };

  const handleConfirmAuthorChange = async () => {
    if (!editingRowId || !selectedAuthorId) return;
    try {
      await editAuthorMutation.mutateAsync({ id: editingRowId, newCollatorStaffId: selectedAuthorId });
      setSnackbar({ open: true, message: 'Job End author updated successfully.', severity: 'success' });
      setEditingRowId(null);
      setSelectedAuthorId(null);
    } catch {
      setSnackbar({ open: true, message: 'Failed to update Job End author.', severity: 'error' });
    }
  };

  const handleCancelEdit = () => {
    setEditingRowId(null);
    setSelectedAuthorId(null);
  };

  // Bulk action handlers
  const handleBulkSendReminder = async () => {
    try {
      const result = await bulkSendReminderMutation.mutateAsync(selectedIds);
      setSnackbar({
        open: true,
        message: buildBulkReminderMessage(result),
        severity: 'success',
      });
    } catch {
      setSnackbar({ open: true, message: 'Failed to send reminders.', severity: 'error' });
    }
  };

  const handleBulkReissue = async () => {
    try {
      const result = await bulkReissueMutation.mutateAsync(selectedIds);
      setSnackbar({
        open: true,
        message: `${result.processedCount} Job End form${result.processedCount !== 1 ? 's' : ''} reissued successfully.`,
        severity: 'success',
      });
    } catch {
      setSnackbar({ open: true, message: 'Failed to reissue Job Ends.', severity: 'error' });
    }
  };

  const handleBulkDelete = async () => {
    try {
      const result = await bulkDeleteMutation.mutateAsync(selectedIds);
      setSnackbar({
        open: true,
        message: `${result.processedCount} Job End form${result.processedCount !== 1 ? 's' : ''} deleted successfully.`,
        severity: 'success',
      });
      setSelectedIds([]);
    } catch {
      setSnackbar({ open: true, message: 'Failed to delete Job Ends.', severity: 'error' });
    }
  };

  const canSendReminder = menuJobEndStatus === 'NotStarted' || menuJobEndStatus === 'Started';
  const canDelete = menuJobEndStatus !== 'Finalised' && menuJobEndStatus !== 'Delivered' && menuJobEndStatus !== 'Deleted';

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
        JOB END ADMIN
      </Typography>

      <Section>
        <SectionHeader>ACTIVE JOB ENDS</SectionHeader>
        <SectionCard>
          {isLoading && !data ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : isError ? (
          <Alert severity="error" sx={{ m: 2 }}>
            Failed to load active job ends.
          </Alert>
        ) : (
          <>
            <TableContainer>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    {canEdit && (
                      <TableCell padding="checkbox" sx={{ bgcolor: 'custom.deepTeal', color: 'white', position: 'sticky', left: 0, zIndex: 4 }}>
                        <Checkbox
                          indeterminate={selectedIds.length > 0 && selectedIds.length < rows.length}
                          checked={rows.length > 0 && selectedIds.length === rows.length}
                          onChange={handleSelectAll}
                          sx={{ color: 'white', '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: 'white' } }}
                        />
                      </TableCell>
                    )}
                    {columns
                      .filter((column) => canEdit || column.id !== 'actions')
                      .map((column) => (
                        <TableCell
                          key={column.id}
                          sx={{
                            minWidth: column.minWidth,
                            bgcolor: 'custom.deepTeal',
                            color: 'white',
                            ...(column.id === 'projectName' && {
                              position: 'sticky',
                              left: canEdit ? CHECKBOX_COL_WIDTH : 0,
                              zIndex: 4,
                              boxShadow: '4px 0 8px -4px rgba(0,0,0,0.15)',
                            }),
                            ...(column.id === 'status' && {
                              position: 'sticky',
                              right: canEdit ? ACTIONS_COL_MIN_WIDTH : 0,
                              zIndex: 4,
                              boxShadow: '-4px 0 8px -4px rgba(0,0,0,0.15)',
                            }),
                            ...(column.id === 'actions' && {
                              position: 'sticky',
                              right: 0,
                              zIndex: 4,
                            }),
                          }}
                        >
                          {column.sortable ? (
                            <TableSortLabel
                              active={sortBy === column.id}
                              direction={sortBy === column.id ? sortDirection : 'asc'}
                              onClick={() => handleSort(column.id)}
                            >
                              {column.label}
                            </TableSortLabel>
                          ) : (
                            column.label
                          )}
                        </TableCell>
                      ))}
                  </TableRow>
                  <FilterRow
                    canEdit={canEdit}
                    columns={columns}
                    inputValues={inputValues}
                    onFilterChange={handleFilterChange}
                    onClearFilter={handleClearFilter}
                    onDateChange={handleDateChange}
                    activeFilterCount={activeFilterCount}
                    onClearAllFilters={handleClearAllFilters}
                  />
                </TableHead>
                <TableBody sx={{ opacity: isFetching ? 0.6 : 1, transition: 'opacity 0.2s' }}>
                  {rows.map((row) => {
                    const isItemSelected = isSelected(row.id);
                    return (
                      <TableRow
                        key={row.id}
                        hover
                        selected={isItemSelected}
                      >
                        {canEdit && (
                          <TableCell padding="checkbox" sx={{ position: 'sticky', left: 0, zIndex: 1, '&&&': { backgroundColor: 'background.paper' } }}>
                            <Checkbox
                              checked={isItemSelected}
                              onChange={() => handleSelectOne(row.id)}
                            />
                          </TableCell>
                        )}
                        <TableCell sx={{
                          position: 'sticky',
                          left: canEdit ? CHECKBOX_COL_WIDTH : 0,
                          zIndex: 1,
                          boxShadow: '4px 0 8px -4px rgba(0,0,0,0.15)',
                          '&&&': { backgroundColor: 'background.paper' },
                        }}>{row.projectName}</TableCell>
                        <TableCell>{formatDateShort(row.dueDate)}</TableCell>
                        <TableCell>{row.performanceFlag || '-'}</TableCell>
                        <TableCell>
                          <Link
                            component={RouterLink}
                            to={`/job-ends/${row.id}`}
                            sx={{ color: 'inherit', textDecoration: 'underline' }}
                          >
                            {row.appraiseeName}
                          </Link>
                        </TableCell>
                        <TableCell>{row.geo}</TableCell>
                        <TableCell>
                          <Chip
                            label={row.tenureInGrade}
                            size="small"
                            sx={{
                              minWidth: 50,
                              justifyContent: 'center',
                              bgcolor: 'grey.200',
                              color: 'text.primary',
                              borderRadius: '16px',
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          {editingRowId === row.id ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <FormControl size="small" sx={{ minWidth: 150 }}>
                                <Select
                                  displayEmpty
                                  value={selectedAuthorId ?? ''}
                                  onChange={(e) => handleAuthorSelect(Number(e.target.value))}
                                  sx={{ bgcolor: 'white' }}
                                >
                                  <MenuItem value="" disabled>
                                    <em>Select author...</em>
                                  </MenuItem>
                                  {eligibleAuthors?.map((author) => (
                                    <MenuItem key={author.staffId} value={author.staffId}>
                                      {author.firstName} {author.lastName}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                              <IconButton size="small" onClick={handleCancelEdit}>
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          ) : (
                            row.jobEndAuthor
                          )}
                        </TableCell>
                        <TableCell sx={{
                          position: 'sticky',
                          right: canEdit ? ACTIONS_COL_MIN_WIDTH : 0,
                          zIndex: 1,
                          boxShadow: '-4px 0 8px -4px rgba(0,0,0,0.15)',
                          '&&&': { backgroundColor: 'background.paper' },
                        }}>
                          <Chip
                            label={formatStatusLabel(row.status)}
                            size="small"
                            sx={{ minWidth: 100, justifyContent: 'center', ...getStatusChipSx(row.status) }}
                          />
                        </TableCell>
                        {canEdit && (
                          <TableCell sx={{ position: 'sticky', right: 0, zIndex: 1, '&&&': { backgroundColor: 'background.paper' } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Tooltip title="Actions">
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleOpenMenu(e, row)}
                                >
                                  <MoreVertIcon />
                                </IconButton>
                              </Tooltip>
                              {editingRowId === row.id && (
                                <>
                                  <IconButton
                                    size="small"
                                    onClick={handleConfirmAuthorChange}
                                    disabled={!selectedAuthorId || editAuthorMutation.isPending}
                                    sx={{ color: 'success.main' }}
                                    data-testid="confirm-edit-author"
                                  >
                                    <CheckCircleIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    onClick={handleCancelEdit}
                                    disabled={editAuthorMutation.isPending}
                                    sx={{ color: 'error.main' }}
                                    data-testid="cancel-edit-author"
                                  >
                                    <CancelIcon fontSize="small" />
                                  </IconButton>
                                </>
                              )}
                            </Box>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                  {rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={canEdit ? columns.length + 1 : columns.length - 1} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                          No active job ends found.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={pageSize}
              onRowsPerPageChange={(e) => {
                setPageSize(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </>
        )}
        </SectionCard>
      </Section>

      <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={handleCloseMenu}>
        <MenuItem onClick={handleEdit}>Edit</MenuItem>
        <MenuItem onClick={handleSendReminder} disabled={!canSendReminder}>
          Send Reminder
        </MenuItem>
        <MenuItem onClick={handleReissue}>Reissue</MenuItem>
        <MenuItem onClick={handleDelete} disabled={!canDelete}>
          Delete
        </MenuItem>
      </Menu>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {canEdit && selectedIds.length > 0 && (
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 2,
            py: 1,
            borderRadius: 2,
            zIndex: 1000,
          }}
        >
          <IconButton size="small" onClick={() => setSelectedIds([])}>
            <CloseIcon fontSize="small" />
          </IconButton>
          <Typography variant="body2" sx={{ fontWeight: 500, mr: 2 }}>
            {selectedIds.length} items selected
          </Typography>
          <Button
            startIcon={<EmailOutlinedIcon />}
            onClick={handleBulkSendReminder}
            sx={{ color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.75rem' }}
          >
            Send Reminder
          </Button>
          <Button
            startIcon={<RefreshIcon />}
            onClick={handleBulkReissue}
            sx={{ color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.75rem' }}
          >
            Reissue
          </Button>
          <Button
            startIcon={<DeleteOutlineIcon />}
            onClick={handleBulkDelete}
            disabled={bulkDeleteDisabled}
            sx={{ color: 'error.main', textTransform: 'uppercase', fontSize: '0.75rem' }}
          >
            Delete
          </Button>
        </Paper>
      )}
    </Box>
  );
}

export default JobEndAdminPage;
